/**
 * 全局 WuKong IM 状态管理
 *
 * @description 管理 WuKong IM 连接状态、未读消息数，并持久化到 localStorage
 * @author system
 * @date 2025-01-21
 */

import { defineStore } from 'pinia'
import WKSDK from 'wukongimjssdk'

// 本地存储键名
const STORAGE_KEY_UNREAD = 'wukong_im_unread_count'

export interface WuKongIMState {
  isConnected: boolean
  isConnecting: boolean
  unreadCount: number
  lastMessageTime: number | null
  sdk: any
}

export const useWuKongIMStore = defineStore('wukongIM', {
  state: (): WuKongIMState => ({
    isConnected: false,
    isConnecting: false,
    unreadCount: 0,
    lastMessageTime: null,
    sdk: null,
  }),

  getters: {
    // 获取未读消息数
    getUnreadCount: (state) => state.unreadCount,
    // 获取连接状态
    getIsConnected: (state) => state.isConnected,
  },

  actions: {
    /**
     * 初始化 - 从 localStorage 恢复未读数
     */
    initFromStorage() {
      if (import.meta.client) {
        try {
          const storedUnread = localStorage.getItem(STORAGE_KEY_UNREAD)
          if (storedUnread) {
            this.unreadCount = parseInt(storedUnread, 10) || 0
            console.log('📦 从 localStorage 恢复未读消息数:', this.unreadCount)
          }
        } catch (error) {
          console.error('读取 localStorage 失败:', error)
        }
      }
    },

    /**
     * 设置未读消息数并持久化
     */
    setUnreadCount(count: number) {
      this.unreadCount = count
      if (import.meta.client) {
        try {
          localStorage.setItem(STORAGE_KEY_UNREAD, String(count))
        } catch (error) {
          console.error('保存未读数到 localStorage 失败:', error)
        }
      }
    },

    /**
     * 增加未读消息数
     */
    incrementUnreadCount(delta: number = 1) {
      this.setUnreadCount(this.unreadCount + delta)
    },

    /**
     * 清除未读消息数
     */
    clearUnreadCount() {
      this.setUnreadCount(0)
    },

    /**
     * 设置连接状态
     */
    setConnected(connected: boolean) {
      this.isConnected = connected
    },

    /**
     * 设置正在连接状态
     */
    setConnecting(connecting: boolean) {
      this.isConnecting = connecting
    },

    /**
     * 设置 SDK 实例
     */
    setSdk(sdk: any) {
      this.sdk = sdk
    },

    /**
     * 更新最后消息时间
     */
    updateLastMessageTime() {
      this.lastMessageTime = Date.now()
    },
  },
})

// 消息回调管理器（全局单例）
class MessageCallbackManager {
  private static instance: MessageCallbackManager
  private callbacks: Array<(message: any) => void> = []
  private connectCallbacks: Array<() => void> = []
  private disconnectCallbacks: Array<() => void> = []

  static getInstance(): MessageCallbackManager {
    if (!MessageCallbackManager.instance) {
      MessageCallbackManager.instance = new MessageCallbackManager()
    }
    return MessageCallbackManager.instance
  }

  addMessageCallback(callback: (message: any) => void) {
    this.callbacks.push(callback)
    return () => {
      const index = this.callbacks.indexOf(callback)
      if (index > -1) {
        this.callbacks.splice(index, 1)
      }
    }
  }

  addConnectCallback(callback: () => void) {
    this.connectCallbacks.push(callback)
    return () => {
      const index = this.connectCallbacks.indexOf(callback)
      if (index > -1) {
        this.connectCallbacks.splice(index, 1)
      }
    }
  }

  addDisconnectCallback(callback: () => void) {
    this.disconnectCallbacks.push(callback)
    return () => {
      const index = this.disconnectCallbacks.indexOf(callback)
      if (index > -1) {
        this.disconnectCallbacks.splice(index, 1)
      }
    }
  }

  notifyMessage(message: any) {
    this.callbacks.forEach((cb) => cb(message))
  }

  notifyConnect() {
    this.connectCallbacks.forEach((cb) => cb())
  }

  notifyDisconnect() {
    this.disconnectCallbacks.forEach((cb) => cb())
  }

  clearAllCallbacks() {
    this.callbacks = []
    this.connectCallbacks = []
    this.disconnectCallbacks = []
  }
}

export const messageCallbackManager = MessageCallbackManager.getInstance()
